/**
 * The dashboard controller (spec §1.3–1.5, §5.10; card D1): open, close, the hold, the modal layer, the keys and the
 * refresh clock, with the DOM behind a `DashView` so Node tests drive it.
 *
 * - **Open**: enters one modal layer (`enterModal`: Esc stack, flight input zeroed, the pointer released) and, with
 *   *Dashboard time* = Pause (the default), adds the hold (`HOLD.dashboard`), so the loop runs no ticks.
 * - **Close by key** (the `dashboard` binding, Backspace at the top): runs synchronously inside the keydown. With
 *   *Keep running* the modal closer calls `ui.relock()` in that gesture. With Pause the hold is removed in the same
 *   keydown and the loop takes the lock back on its next frame (its hold-end request, which the keydown's transient
 *   activation covers, exactly as the docked Launch click does); calling `ui.relock()` as well would send a second
 *   lock request while the first is pending.
 * - **Esc** goes through the modal's Esc handler (the loop's `consumeEscape`): one level back, and at the top it is
 *   not consumed, so the loop opens the pause menu over the dashboard, which stays open underneath. Resume returns
 *   to it.
 * - **While paused** (the menu is on top) the controller takes no keys.
 * - **Focus** (§1.6): arrows/WASD, F, R and PgUp/PgDn go to the open section (`DashNavPort`, `ui/dashboard/focus.ts`);
 *   a move at the top level drops into the list. Enter/Space at the list or detail level reach the section first
 *   (activate), and only unhandled go a level in.
 * - **Deep links** (§1.2; when `deepLink` resolves a key, i.e. `dashboard.json deepLinks`): N/K/U/L open the
 *   dashboard at their tab; the key that opened it closes it again, another link key moves there.
 * - **Pad** (`padToggle`, the View tap): opens or closes without a relock (a pad press is no user activation; the
 *   loop skips its hold-end request after pad input, §1.5).
 * - **Closed**: `frame` returns at once; the key listener is the only cost.
 */
import type { UiState } from '../../app/feature'
import { enterModal } from '../../app/ui'
import { closeDash, createDashModel, dashTabId, keyToDashInput, openDash, stepDash, type DashLanding, type DashModel } from '../../ui/dashboard/model'
import { keyToDashFocusInput, type DashNavPort } from '../../ui/dashboard/focus'
import { DASH_TABS, dashSections, isDashTab, type DashContext, type DashTarget } from '../../ui/dashboard/registry'
import type { OverviewData } from '../../ui/dashboard/tabs/overview/model'

/** Settings › Interface › *Dashboard time* (spec §5.6). */
export type DashTimeMode = 'pause' | 'keepRunning'

/** What the view renders (a new object per render; renders happen on input and at most `refreshHz` while open). */
export interface DashSnapshot {
  readonly tab: number
  readonly section: number
  readonly depth: number
  readonly focus: string | undefined
  readonly time: DashTimeMode
  readonly ctx: DashContext
  /** Section id → unread count (polled at `badgeHz`). */
  readonly badges: Readonly<Record<string, number>>
  /** The Overview cards and the header readouts (W4 C1), polled with the snapshot; null without a source. */
  readonly overview: OverviewData | null
}

export interface DashView {
  show(): void
  hide(): void
  render(s: DashSnapshot): void
}

export interface DashControllerOptions {
  readonly ui: UiState
  readonly holds: Set<string>
  /** The hold reason (`HOLD.dashboard`). */
  readonly holdId: string
  readonly view: DashView
  /** The keys bound to `dashboard` (open and close). */
  readonly keys: readonly string[]
  readonly timeMode: () => DashTimeMode
  readonly dashCtx: () => DashContext
  readonly refreshHz: number
  readonly badgeHz: number
  /** Focus inputs to the open section. */
  readonly nav: DashNavPort
  /** A deep-link key's landing, or null (always null while `dashboard.json deepLinks` is off). */
  readonly deepLink?: (code: string) => DashTarget | null
  /** The Overview and header data (W4 C1); read with every snapshot, so at most `refreshHz` while open. */
  readonly overview?: () => OverviewData | null
}

export interface DashController {
  readonly model: DashModel
  readonly isOpen: boolean
  /** True while the hold is on (Pause mode, open). */
  readonly holding: boolean
  open(at?: DashTarget): void
  /** `relock` true only from inside a user gesture. */
  close(relock: boolean): void
  /** Mouse: a tab (0..7) or a section of the open tab. */
  selectTab(index: number): void
  selectSection(index: number): void
  /** The capture-phase keydown; true when the dashboard used (or swallowed) the key. */
  keydown(code: string, repeat: boolean): boolean
  /** The pad's View tap (the `dashboard` edge): open or close, never relocking. */
  padToggle(): void
  /** The game paused (the menu is on top) or resumed. */
  setPaused(paused: boolean): void
  /** The time setting may have changed: re-applies the hold while open. */
  applyTimeMode(): void
  /** Once per frame: returns at once while closed; re-renders on snapshot change at `refreshHz` while open. */
  frame(dt: number): void
}

export function createDashController(o: DashControllerOptions): DashController {
  const model = createDashModel()
  let exitModal: ((relock?: boolean) => void) | null = null
  let holding = false
  let paused = false
  let refreshAcc = 0
  let badgeAcc = 0
  let badges: Record<string, number> = {}
  let lastKey = ''
  /** The deep-link key that opened (or last moved) the dashboard; pressing it again closes. */
  let linkCode = ''

  const sectionCount = (tab: number): number => (tab === 0 ? 0 : dashSections(DASH_TABS[tab], o.dashCtx().docked).length)

  const snapshot = (): DashSnapshot => ({
    tab: model.tab,
    section: model.sections[model.tab],
    depth: model.depth,
    focus: model.focus,
    time: holding ? 'pause' : 'keepRunning',
    ctx: o.dashCtx(),
    badges,
    overview: o.overview?.() ?? null,
  })

  const keyOf = (s: DashSnapshot): string => {
    let k = `${s.tab}|${s.section}|${s.depth}|${s.focus ?? ''}|${s.time}|${String(s.ctx.docked)}|${String(s.ctx.career)}`
    for (const id in s.badges) k += `|${id}=${s.badges[id]}`
    if (s.overview !== null) k += `|ov=${s.overview.key}`
    return k
  }

  const render = (force: boolean): void => {
    const s = snapshot()
    const k = keyOf(s)
    if (!force && k === lastKey) return
    lastKey = k
    o.view.render(s)
  }

  const pollBadges = (): void => {
    const ctx = o.dashCtx()
    const next: Record<string, number> = {}
    for (let t = 1; t < DASH_TABS.length; t++) {
      for (const s of dashSections(DASH_TABS[t], ctx.docked)) {
        const n = s.badge?.(ctx) ?? 0
        if (n > 0) next[s.id] = n
      }
    }
    badges = next
  }

  const resolve = (at: DashTarget): DashLanding | undefined => {
    const docked = o.dashCtx().docked
    if (at.section !== undefined) {
      for (let t = 1; t < DASH_TABS.length; t++) {
        const i = dashSections(DASH_TABS[t], docked).findIndex((s) => s.id === at.section)
        if (i >= 0) return { tab: t, section: i, focus: at.focus }
      }
    }
    if (at.tab !== undefined && isDashTab(at.tab)) return { tab: DASH_TABS.indexOf(at.tab), section: -1, focus: at.focus }
    return undefined
  }

  const setHold = (on: boolean): void => {
    if (on === holding) return
    holding = on
    if (on) o.holds.add(o.holdId)
    else o.holds.delete(o.holdId)
  }

  const canOpen = (): boolean => o.holds.size === 0 && o.ui.modal === 0

  const onEscape = (): boolean => {
    const out = stepDash(model, 'escape', sectionCount)
    if (out === 'changed') render(false)
    // 'menu': not consumed, so the loop opens the pause menu over the dashboard.
    return out === 'changed'
  }

  const c: DashController = {
    model,
    get isOpen(): boolean {
      return model.open
    },
    get holding(): boolean {
      return holding
    },

    open(at?: DashTarget): void {
      const landing = at === undefined ? undefined : resolve(at)
      if (model.open) {
        if (landing !== undefined) {
          openDash(model, landing)
          render(false)
        }
        return
      }
      openDash(model, landing)
      linkCode = ''
      exitModal = enterModal(o.ui, onEscape)
      setHold(o.timeMode() === 'pause')
      refreshAcc = 0
      badgeAcc = 0
      pollBadges()
      render(true)
      o.view.show()
    },

    close(relock: boolean): void {
      if (!model.open) return
      closeDash(model)
      o.view.hide()
      const held = holding
      setHold(false)
      const closer = exitModal
      exitModal = null
      // With the hold, the loop's hold-end request re-takes the lock (see the header); otherwise relock here.
      closer?.(relock && !held)
    },

    selectTab(index: number): void {
      if (stepDash(model, index, sectionCount) === 'changed') render(false)
    },

    selectSection(index: number): void {
      if (!model.open || index < 0 || index >= sectionCount(model.tab)) return
      model.sections[model.tab] = index
      model.depth = 0
      model.focus = undefined
      render(false)
    },

    keydown(code: string, repeat: boolean): boolean {
      if (paused) return false
      const link = o.deepLink?.(code) ?? null
      if (!model.open) {
        if (repeat || (link === null && !o.keys.includes(code))) return false
        if (!canOpen()) return false
        c.open(link ?? undefined)
        if (link !== null) linkCode = code
        return true
      }
      if (o.keys.includes(code)) {
        if (!repeat) c.close(true)
        return true
      }
      if (link !== null) {
        if (repeat) return true
        if (code === linkCode) c.close(true)
        else {
          c.open(link)
          linkCode = code
        }
        return true
      }
      if (keyToDashInput(code, false) !== null) {
        const input = keyToDashInput(code, repeat)
        if (input === null) return true
        // Enter/Space below the top level: the section's focused item first.
        if (input === 'enter' && model.depth > 0 && o.nav.dispatch('activate')) return true
        const out = stepDash(model, input, sectionCount)
        if (out === 'changed') render(false)
        else if (out === 'close') c.close(true)
        return true
      }
      const fi = keyToDashFocusInput(code, repeat)
      if (fi === null) return keyToDashFocusInput(code, false) !== null
      if (model.tab !== 0 && o.nav.dispatch(fi) && model.depth === 0) {
        model.depth = 1
        render(false)
      }
      return true
    },

    padToggle(): void {
      if (paused) return
      if (model.open) c.close(false)
      else if (canOpen()) c.open()
    },

    setPaused(p: boolean): void {
      paused = p
    },

    applyTimeMode(): void {
      if (!model.open) return
      setHold(o.timeMode() === 'pause')
      render(false)
    },

    frame(dt: number): void {
      if (!model.open) return
      refreshAcc += dt
      badgeAcc += dt
      if (badgeAcc >= 1 / o.badgeHz) {
        badgeAcc = 0
        pollBadges()
      }
      if (refreshAcc < 1 / o.refreshHz) return
      refreshAcc = 0
      render(false)
    },
  }
  return c
}

/** The open tab's id (tests and the debug API). */
export function openTabId(c: DashController): string {
  return dashTabId(c.model)
}
